import React, { useState, useEffect } from 'react';
import {debounce, filter, orderBy, reject} from 'lodash';

import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Slide from '@material-ui/core/Slide';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';

import {onlineHubHost} from '../../config';
import { useFetchResource } from '../../helpers/hooks';

import IconInput from '../IconInput';
import Table from '../Table';
import BsnSetupForm from './components/BsnSetupForm';

import './BsnSetupList.css';

const NewButton = withStyles((theme) => ({
  root: {
    margin: theme.spacing(1)
  }
}))(Button);

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const columns = [
  { id: 'name', label: 'Name', sort: true, minWidth: 170 },
  { id: 'network', label: 'Network', sort: true, minWidth: 170 },
  { id: 'payload.bDeploy.url', label: 'Autorun Url', sort: true, minWidth: 170, link: true }
];

function sortItems(items, sortBy, sortOrder) {
  if (sortBy === 'autorunUrl') {
    return orderBy(items, [item => item.payload && item.payload.bDeploy.url.toLowerCase()], [sortOrder]);
  }
  return orderBy(items, [item => item[sortBy].toLowerCase()], [sortOrder]);
}

function searchItems(items, search) {
  const normalizedSearch = search.toLowerCase();
  return filter(items, item => (
    item.name.toLowerCase().includes(normalizedSearch)
    || item.network.toLowerCase().includes(normalizedSearch)
    || (item.payload ? item.payload.bDeploy.url.toLowerCase().includes(normalizedSearch) : false)
  ))
}

function getFormData({autorunFolderFile, ...data}) {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key]);
  });
  formData.append('file', autorunFolderFile);
  return formData;
}

export default function BsnSetupList() {
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortedItems, setSortedItems] = useState(null);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(null);
  const [newModalActive, setNewModalActive] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [duplicateItemsIds, setDuplicateItemsIds] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState({});

  const {data} = useFetchResource(`${onlineHubHost}/app/bsnSetups`);
  const items = filteredItems || sortedItems || [];

  useEffect(() => {
    if (data && !sortedItems) {
      setSortedItems(sortItems(data || [], sortBy, sortOrder));
    }
  }, [data, sortedItems, sortBy, sortOrder])

  function handleSortClick(newSortBy) {
    if (newSortBy === sortBy) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      setSortedItems(sortItems(items, sortBy, newSortOrder));
    } else {
      setSortBy(newSortBy);
      setSortedItems(sortItems(items, newSortBy, sortOrder));
    }
  }

  const handleSearchChange = debounce((value) => {
    const source = sortedItems || data || [];
    setFilteredItems(value.length ? searchItems(source, value) : null);
    setSearch(value);
  }, 500)

  function handleNewClick() {
    setNewModalActive(!newModalActive);
  }

  function handleAddClose() {
    setNewModalActive(false);
    setEditItemId(null);
  }

  function handleAddSubmit({autorunFolderFile, ...data}) {
    const body = autorunFolderFile ? getFormData({autorunFolderFile, ...data}) : JSON.stringify(data);
    const headers = {
      authorization: `Bearer ${localStorage.getItem('token')}`
    };
    if (!autorunFolderFile) {
      headers['content-type'] = 'application/json';
    }
    const url = duplicateItemsIds
      ? `${onlineHubHost}/app/bsnSetups/duplicate?ids=${duplicateItemsIds.join(',')}`
      : `${onlineHubHost}/app/bsnSetups${editItemId ? `/${editItemId}` : ''}`
    fetch(url, {
      method: !editItemId || duplicateItemsIds ? 'POST' : 'PATCH',
      headers,
      body
    })
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error(`Unable to ${editItemId ? 'update' : 'create a'} BSN Setup`);
    })
    .then((result) => {
      const newSortedItems = sortItems((editItemId
        ? reject(items, {id: editItemId})
        : items).concat(Array.isArray(result) ? result : [result])
      , sortBy, sortOrder);
      setSortedItems(newSortedItems);
      if (search) {
        setFilteredItems(searchItems(newSortedItems, search));
      }
      setNewModalActive(false);
      setEditItemId(null);
      setDuplicateItemsIds(null)
      setSnackbarMessage({show: true, severity: 'success', text: `BSN Setup has been ${editItemId ? 'updated' : 'created'}.`});
    })
    .catch((err) => {
      setSnackbarMessage({show: true, severity: 'error', text: `BSN Setup has not been ${editItemId ? 'updated' : 'created'}.`});
      console.error(err);
    })
  }

  function handleItemEdit(id) {
    setEditItemId(id);
    setNewModalActive(true);
  }

  function handleItemsDuplicate(ids) {
    setDuplicateItemsIds(ids);
    setNewModalActive(true);
  }

  function handleItemsDelete(ids) {
    fetch(`${onlineHubHost}/app/bsnSetups`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ids})
    })
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error('Unable to delete BSN Setups');
    })
    .then((result) => {
      const newSortedItems = sortItems(reject(items, item => result.ids.includes(item.id)), sortBy, sortOrder);
      setSortedItems(newSortedItems);
      if (search) {
        setFilteredItems(searchItems(newSortedItems, search));
      }
      setSnackbarMessage({show: true, severity: 'success', text: `BSN Setup ${ids.length > 1 ? 'have' : 'has'} been deleted.`});
    })
    .catch((err) => {
      setSnackbarMessage({show: true, severity: 'error', text: `Unable to delete ${ids.length ? 'those setups' : 'this setup'}.`})
      console.error(err);
    })
  }

  return (
    <div className="BsnSetupList">
      <div className="BsnSetupList-header">
        <NewButton
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewClick}
        >
          New
        </NewButton>
        <IconInput
          icon={<SearchIcon />}
          label="Search"
          onChange={handleSearchChange}
        />
      </div>
      <Table
        columns={columns}
        items={items}
        sortBy={sortBy}
        sortOrder={sortOrder}
        duplicateButton
        onSortClick={handleSortClick}
        onEdit={handleItemEdit}
        onDuplicate={handleItemsDuplicate}
        onDelete={handleItemsDelete}
      />
      <BsnSetupForm
        itemId={editItemId}
        items={items}
        active={newModalActive}
        duplicate={!!duplicateItemsIds}
        onClose={handleAddClose}
        onSubmit={handleAddSubmit}
      />
      <Snackbar
        open={snackbarMessage.show}
        autoHideDuration={3000}
        TransitionComponent={SlideTransition}
        onClose={() => setSnackbarMessage({...snackbarMessage, show: false})}
      >
        <Alert severity={snackbarMessage.severity} onClose={() => setSnackbarMessage({...snackbarMessage, show: false})}>
          {snackbarMessage.text}
        </Alert>
      </Snackbar>
    </div>
  );
}
